import { useState } from "react";
import { ChevronDown, FileText, PlayCircle, CheckCircle, Lock, Circle } from "lucide-react";

export default function CurriculumSidebar({
  modules,
  currentLecture,
  onSelectLecture,
  completedLectures = [],
  currentWeek,
}) {
  const [expandedWeeks, setExpandedWeeks] = useState(
    modules?.length > 0 ? [currentWeek || modules[0]?.week || 1] : []
  );

  const toggleWeek = (week) => {
    setExpandedWeeks((prev) =>
      prev.includes(week) ? prev.filter((w) => w !== week) : [...prev, week]
    );
  };

  // Auto-expand current week
  if (currentWeek && !expandedWeeks.includes(currentWeek)) {
    setExpandedWeeks((prev) => [...prev, currentWeek]);
  }

  const totalLecturesInCourse = modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0) || 0;
  const completedCount = completedLectures.length;

  return (
    <div className="h-full flex flex-col bg-white border-r border-slate-200">
      {/* Header */}
      <div className="p-3 border-b border-slate-200">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Course Content</h3>
        <p className="text-[10px] text-slate-400 mt-0.5">
          {completedCount}/{totalLecturesInCourse} completed
        </p>
      </div>

      {/* Module list */}
      <div className="flex-1 overflow-y-auto">
        {modules?.map((mod) => {
          const isExpanded = expandedWeeks.includes(mod.week);
          const moduleLessons = mod.lessons || [];
          const moduleCompleted = moduleLessons.filter((l) =>
            completedLectures.includes(`${mod.week}-${l.title}`)
          ).length;

          return (
            <div key={mod.week} className="border-b border-slate-100">
              {/* Module header */}
              <button
                onClick={() => toggleWeek(mod.week)}
                className={`w-full flex items-center gap-2 p-2.5 text-left hover:bg-slate-50 transition-colors ${
                  currentWeek === mod.week ? "bg-emerald-50" : ""
                }`}
              >
                <ChevronDown
                  className={`w-3 h-3 text-slate-400 flex-shrink-0 transition-transform ${
                    isExpanded ? "" : "-rotate-90"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-slate-700 truncate">
                    Section {mod.week}: {mod.title}
                  </p>
                  <p className="text-[9px] text-slate-400">
                    {moduleCompleted}/{moduleLessons.length} | {mod.quiz ? "Quiz" : ""}
                  </p>
                </div>
              </button>

              {/* Lessons */}
              {isExpanded && (
                <div className="bg-slate-50/50">
                  {moduleLessons.map((lesson, idx) => {
                    const lectureKey = `${mod.week}-${lesson.title}`;
                    const isActive = currentLecture === `${mod.week}-${idx}`;
                    const isCompleted = completedLectures.includes(lectureKey);
                    const hasVideo = !!lesson.video_url;
                    const hasContent = !!lesson.content;

                    return (
                      <button
                        key={idx}
                        onClick={() => onSelectLecture(mod.week, idx)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-100 transition-colors ${
                          isActive ? "bg-emerald-100 border-l-2 border-emerald-500" : "border-l-2 border-transparent"
                        }`}
                      >
                        {/* Icon */}
                        {isCompleted ? (
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        ) : hasVideo ? (
                          <PlayCircle className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        ) : hasContent ? (
                          <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        ) : (
                          <Circle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                        )}

                        <div className="flex-1 min-w-0">
                          <p className={`text-[11px] truncate ${
                            isActive ? "font-medium text-emerald-800" : "text-slate-600"
                          }`}>
                            {lesson.title}
                          </p>
                          <p className="text-[9px] text-slate-400">
                            {lesson.duration_minutes || 0} min
                            {isCompleted && " · Completed"}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
